import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Mail, Phone, MapPin, Send, CheckCircle2, Clock, Building2,
  UploadCloud, FileText, X, AlertCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { company, services } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_MB = 25;
const MAX_FILES = 12;
const ALLOWED = [
  // Drawings & CAD
  '.pdf', '.dwg', '.dxf', '.dwf', '.dwfx', '.rvt', '.rfa', '.ifc', '.skp',
  '.nwd', '.nwc', '.fbx', '.obj', '.stp', '.step',
  // Archives
  '.zip', '.rar', '.7z',
  // Images
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff', '.webp', '.heic',
  // Office
  '.xls', '.xlsx', '.xlsm', '.csv', '.doc', '.docx', '.ppt', '.pptx',
  '.txt', '.rtf', '.odt', '.ods',
];

function prettyBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function Contact() {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', service: '', message: '',
  });

  const addFiles = (incoming) => {
    setError('');
    const list = Array.from(incoming || []);
    const next = [...files];
    for (const f of list) {
      if (next.length >= MAX_FILES) {
        setError(`Max ${MAX_FILES} files. Zip extras if needed.`);
        break;
      }
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED.includes(ext)) {
        setError(`File type ${ext} not supported. Allowed: PDF, DWG, DXF, RVT, ZIP, images, Excel, Word.`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        setError(`${f.name} is larger than ${MAX_FILE_MB} MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files) addFiles(e.dataTransfer.files);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name || !form.email || !form.message) {
      setError('Please fill out name, email and project details.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('company', form.company);
      fd.append('service', form.service);
      fd.append('message', form.message);
      files.forEach(f => fd.append('files', f));
      const res = await axios.post(`${API}/contact`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setSubmitted(true);
      setFiles([]);
      setForm({ name: '', email: '', phone: '', company: '', service: '', message: '' });
      toast({
        title: 'Quote request received',
        description: res.data?.email_sent
          ? 'A senior estimator will reach out within 4 business hours.'
          : 'Saved — our team will contact you shortly.',
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Submission failed. Try again or call us directly.';
      setError(msg);
      toast({ title: 'Submission failed', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative pt-40 pb-20 bg-[#0B1B33] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-4">Contact us</div>
          <h1 className="text-5xl md:text-6xl font-bold text-white tracking-tight leading-[1.05] max-w-3xl">
            Upload your drawings.<br />
            <span className="text-amber-400">We’ll respond in 4 hours.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            Every quote request is reviewed by a senior estimator. Attach PDF, DWG, DXF, RVT or ZIP files —
            we’ll reply with scope, lead time and a fixed fee. No obligation.
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-8">
            <Card className="p-8 md:p-10 rounded-3xl bg-white border-slate-100 shadow-sm">
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 px-3 py-1">Request a quote</Badge>
              <h2 className="mt-4 text-3xl font-bold text-[#0B1B33] tracking-tight">Tell us about the project.</h2>
              <p className="mt-2 text-slate-600">Fields marked with * are required. Email goes to <b>{company.email}</b>.</p>

              {submitted ? (
                <div className="mt-8 p-8 rounded-2xl bg-amber-50 border border-amber-200 text-center">
                  <CheckCircle2 className="w-12 h-12 text-amber-500 mx-auto" />
                  <h3 className="mt-4 text-xl font-bold text-[#0B1B33]">Got it — thank you.</h3>
                  <p className="mt-2 text-slate-700">
                    Your request and any attachments have been emailed to our team at {company.email}.
                    A senior estimator will respond within 4 business hours.
                  </p>
                  <Button className="mt-6 bg-[#0B1B33] hover:bg-[#11264a] text-white rounded-xl" onClick={() => setSubmitted(false)}>
                    Submit another
                  </Button>
                </div>
              ) : (
                <form className="mt-8 space-y-5" onSubmit={onSubmit}>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Full name *</label>
                      <Input className="mt-2 h-11 rounded-xl" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Email *</label>
                      <Input className="mt-2 h-11 rounded-xl" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@company.com" required />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Phone</label>
                      <Input className="mt-2 h-11 rounded-xl" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(888) 506-3888" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700">Company</label>
                      <Input className="mt-2 h-11 rounded-xl" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Acme Construction" />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Service of interest</label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger className="mt-2 h-11 rounded-xl">
                        <SelectValue placeholder="Pick a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.slug} value={s.slug}>{s.title} · {s.category}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700">Project details *</label>
                    <Textarea className="mt-2 rounded-xl min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Project type, location, square footage, drawing set issue date, bid deadline, scope of work, anything else we should know…" required />
                  </div>

                  {/* Drawing upload */}
                  <div>
                    <label className="text-sm font-medium text-slate-700">Drawings & documents (optional)</label>
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}
                      onClick={() => fileRef.current?.click()}
                      className={`mt-2 rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition ${
                        dragOver ? 'border-amber-500 bg-amber-50' : 'border-slate-300 bg-slate-50 hover:border-amber-400 hover:bg-amber-50/40'
                      }`}
                    >
                      <input
                        ref={fileRef}
                        type="file"
                        multiple
                        className="hidden"
                        accept={ALLOWED.join(',')}
                        onChange={(e) => addFiles(e.target.files)}
                      />
                      <UploadCloud className={`w-10 h-10 mx-auto ${dragOver ? 'text-amber-500' : 'text-slate-400'}`} />
                      <div className="mt-3 font-semibold text-[#0B1B33]">
                        Drag &amp; drop or <span className="text-amber-600 underline">browse</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        PDF, DWG, DXF, RVT, IFC, SKP, ZIP, RAR, images, Excel, Word, PowerPoint · up to {MAX_FILE_MB} MB / file · max {MAX_FILES} files (30 MB total)
                      </div>
                    </div>

                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {files.map((f, i) => (
                          <div key={i} className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-2.5">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-[#0B1B33] truncate">{f.name}</div>
                                <div className="text-xs text-slate-500">{prettyBytes(f.size)}</div>
                              </div>
                            </div>
                            <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500 transition" aria-label="Remove file">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl w-full md:w-auto disabled:opacity-60"
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <span className="w-4 h-4 mr-2 border-2 border-[#0B1B33] border-t-transparent rounded-full animate-spin" />
                        Sending…
                      </span>
                    ) : (
                      <span className="flex items-center">Send request <Send className="w-4 h-4 ml-2" /></span>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500">By submitting you agree to be contacted about your request. We never share your information.</p>
                </form>
              )}
            </Card>
          </div>

          {/* Info */}
          <div className="lg:col-span-4 space-y-5">
            <Card className="p-7 rounded-2xl border-slate-100 bg-[#0B1B33] text-white">
              <h3 className="font-bold text-lg">Reach us directly</h3>
              <p className="mt-2 text-sm text-slate-300">Bid deadline today? Skip the form — call or email.</p>
              <div className="mt-6 space-y-4">
                <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-4 hover:text-amber-400 transition">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Phone</div>
                    <div className="font-semibold">{company.phone}</div>
                  </div>
                </a>
                <a href={`mailto:${company.email}`} className="flex items-center gap-4 hover:text-amber-400 transition">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Email</div>
                    <div className="font-semibold">{company.email}</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">Hours</div>
                    <div className="font-semibold">Mon – Sat · 8am – 8pm EST</div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-7 rounded-2xl border-slate-100 bg-white">
              <Building2 className="w-6 h-6 text-amber-500" />
              <h3 className="mt-3 font-bold text-[#0B1B33]">Our offices</h3>
              <ul className="mt-5 space-y-5">
                {company.addresses.map((a, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500">{a.label}</div>
                      <div className="text-sm text-slate-700 mt-0.5">{a.value}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
}
